import type { NodeV2 } from '@vedalib/editor'
import { useSize } from 'ahooks'
import React, { useMemo, useRef } from 'react'

import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { ABSOLUTE_PORTAL_GAP } from 'components/Editor/components/AbsolutePortal/constants'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { useProjectContext } from 'services/Store/Project/hooks'
import {
  getIsActiveAltKey,
  getIsDestination,
  getIsHighlighted,
  getIsShowGrid,
  getNodeStyle,
  mergeNodeStyle,
} from 'services/Store/Project/selectors'
import type { Block, IBlockMode } from 'services/Store/Project/types'

import { isAbsolute, isFlex } from '../../guards'
import { useShellSize } from '../../useShellSize'
import * as s from './NodeV2SvgInfo.module.scss'
import SvgBoxModel from './SvgBoxModel'
import SvgLayoutAbsolute from './SvgLayoutAbsolute'

interface INodeV2SvgInfoProps {
  mode: IBlockMode
  block: Block
  parent: NodeV2 | null
  node: NodeV2
  selected: NodeV2[]
}

const NodeV2SvgInfo: React.FC<INodeV2SvgInfoProps> = ({ mode, block, parent, node, selected }) => {
  const ref = useRef<SVGSVGElement>(null)
  const refSize = useSize(ref)
  const isAlt = useProjectContext(getIsActiveAltKey)
  const style = useProjectContext(getNodeStyle, block.uuid, node.id)
  const parentStyle = useProjectContext(getNodeStyle, block.uuid, parent?.id || null)
  const isHighlighted = useProjectContext(getIsHighlighted, node.id)
  // const isActive = useProjectContext(getIsActiveNode, node.id)
  // const isTarget = useProjectContext(getIsTarget, node.id)
  // const isResize = useProjectContext(getIsResize)
  const isDestination = useProjectContext(getIsDestination, node.id)
  const isShowGrid = useProjectContext(getIsShowGrid, block.uuid, node.id)
  const size = useShellSize(parentStyle, style)
  const isEveryAbsolute = useMemo(
    () => selected.every((n) => mergeNodeStyle(n.style, mode.deviceMode).flexFlow === 'absolute'),
    [mode.deviceMode, selected],
  )

  return (
    <>
      <AbsolutePortal name='selection' recalculatingKey={block}>
        <svg className={s.svg} ref={ref}>
          {isAlt && isHighlighted && refSize && <SvgBoxModel size={refSize} styles={style} />}
          {(isAbsolute(style) || (isFlex(style) && isEveryAbsolute)) &&
            refSize &&
            (isAlt || isDestination || isShowGrid) && (
              <SvgLayoutAbsolute
                block={block}
                mode={mode}
                node={node}
                selected={selected}
                size={refSize}
              />
            )}
        </svg>
      </AbsolutePortal>
      {size && isHighlighted && isAlt /* || (isActive && isResize && isTarget) */ && (
        <AbsolutePortal
          chain='off'
          name='title'
          offset={`${ABSOLUTE_PORTAL_GAP}px 0`}
          placement='bottomLeft'
          recalculatingKey={block}
          zIndex={1}
        >
          <div className={s.size}>
            <Typography color='inherit' rows={1} shrink={0} size={KitSize.XS}>
              {size.width} <Icon name='otherClose' /> {size.height}
            </Typography>
            {(size.minWidth || size.minHeight) && (
              <Typography color='inherit' rows={1} shrink={0} size={KitSize.XS}>
                (min){size.minWidth || 'unset'} <Icon name='otherClose' />{' '}
                {size.minHeight || 'unset'}
              </Typography>
            )}
            {(size.maxWidth || size.maxHeight) && (
              <Typography color='inherit' rows={1} shrink={0} size={KitSize.XS}>
                (max){size.maxWidth || 'unset'} <Icon name='otherClose' />{' '}
                {size.maxHeight || 'unset'}
              </Typography>
            )}
          </div>
        </AbsolutePortal>
      )}
    </>
  )
}

export default React.memo(NodeV2SvgInfo)
